'use client'

import {
  type DehydratedState,
  HydrationBoundary,
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query'
import { type PropsWithChildren, useState } from 'react'

type QueryProviderProps = PropsWithChildren<{
  dehydratedState?: DehydratedState
}>

function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false
      }
    }
  })
}

// Locale swaps remount `app/[locale]/layout.tsx` (and this provider). A fresh
// QueryClient per remount wiped /users/me and points history, so the profile
// briefly fell back to a skeleton and tab panels jumped while refetching.
// Keep one browser client for the tab session (TanStack Next.js pattern).
let browserQueryClient: QueryClient | undefined

function getQueryClient() {
  if (typeof window === 'undefined') {
    return createQueryClient()
  }

  if (!browserQueryClient) {
    browserQueryClient = createQueryClient()
  }

  return browserQueryClient
}

const QueryProvider = ({ children, dehydratedState }: QueryProviderProps) => {
  const [queryClient] = useState(getQueryClient)

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={dehydratedState}>{children}</HydrationBoundary>
    </QueryClientProvider>
  )
}

export default QueryProvider
